import { Sim } from '../../game/simulation';
import { t, plural } from '../strings';
import { ICON } from '../icons';

/**
 * Zones manager — sidebar overlay opened by the Hotbar's plot button.
 *
 * Lists every adopted plot as a card. From here the player can:
 *   • Click a card → dive into that plot's view (with the drop animation).
 *   • "+ New zone" → start a fresh selection; click tiles in the world to
 *     add them; "Adopt" saves the plot and returns to the card list.
 *   • "Edit tiles" on a card → click world tiles to add / remove from
 *     that plot's membership. "Done" returns to the card list.
 *   • Rename or delete a plot.
 *
 * Plot view itself no longer has an in-place edit affordance — everything
 * routes through this panel.
 *
 * The body gets a `.zones-mode` class while the panel is open so the
 * rest of the HUD dims out of the way.
 */
export class ZonesPanel {
  private root: HTMLDivElement;
  private lastHash = '';
  private newPlotNameInput: HTMLInputElement | null = null;

  constructor(parent: HTMLElement, private sim: Sim) {
    this.root = document.createElement('div');
    this.root.className = 'ui-zones';
    this.root.hidden = true;
    parent.appendChild(this.root);
    this.root.addEventListener('click', this.onClick);
    this.root.addEventListener('input', this.onInput);
    this.root.addEventListener('change', this.onInput);
    sim.bus.on(e => {
      switch (e.type) {
        case 'zonesModeChanged':
        case 'plotsChanged':
        case 'plotSelectChanged':
        case 'plotEntered':
        case 'plotExited':
        case 'plotTileRemoved':
          this.lastHash = '';        // force a refresh next tick
          break;
      }
    });
  }

  update(_dt: number) {
    const open = !!this.sim.state.zonesMode;
    // Sync visibility every frame — cheap, and the previous `===` check
    // had the comparison inverted so the panel stayed hidden forever.
    if (this.root.hidden !== !open) {
      this.root.hidden = !open;
      document.body.classList.toggle('zones-mode', open);
    }
    if (!open) return;
    const hash = this.computeHash();
    if (hash === this.lastHash) return;
    this.lastHash = hash;
    this.render();
  }

  /** Cheap dirty-check so we don't re-render every frame. */
  private computeHash(): string {
    const s = this.sim.state;
    const plots = s.plots.map(p => `${(p as any).name ?? ''}|${p.tiles.length}`).join('||');
    const selN = s.selecting?.tiles.length ?? -1;
    return `${plots}|${s.zonesEditPlotIdx ?? -1}|${selN}`;
  }

  private render() {
    const s = this.sim.state;
    const plots = s.plots;
    const editingIdx = s.zonesEditPlotIdx ?? null;
    const selecting = s.selecting;
    const cards = plots.map((p, idx) => {
      const name = (p as any).name ?? `Plot ${idx + 1}`;
      const tileN = p.tiles.length;
      const isSelected = editingIdx === idx;
      return `
        <div class="ui-zone-card ${isSelected ? 'ui-zone-card--selected' : ''}" data-plot-idx="${idx}">
          <div class="ui-zone-card__head">
            <span class="ui-zone-card__icon glyph">${ICON.plot}</span>
            <input class="ui-zone-card__name" data-rename-idx="${idx}" value="${escapeAttr(name)}" />
          </div>
          <div class="ui-zone-card__meta">${t('zones.card.tiles', { n: tileN, s: plural(tileN, 's') })}</div>
          ${isSelected ? `<div class="ui-zone-card__hint">${t('zones.card.editing')}</div>` : ''}
          <div class="ui-zone-card__actions">
            <button class="ui-zone-card__btn ui-zone-card__btn--primary" data-act="enter" data-idx="${idx}">Enter</button>
            <button class="ui-zone-card__btn ui-zone-card__btn--danger" data-act="delete" data-idx="${idx}">${t('zones.btn.delete')}</button>
          </div>
        </div>
      `;
    }).join('');

    // The "new zone" section either shows the +New button OR the in-flight
    // selection state (tile count + Adopt/Cancel). They never coexist.
    let newZone = '';
    if (selecting) {
      const n = selecting.tiles.length;
      const placeholder = t('zones.new.namePlaceholder', { n: plots.length + 1 });
      newZone = `
        <div class="ui-zone-new">
          <div class="ui-zone-new__title">${t('zones.new.title')}</div>
          <div class="ui-zone-new__tip">${t('zones.new.tip')}</div>
          <input class="ui-zone-new__name" data-id="new-name" placeholder="${escapeAttr(placeholder)}" />
          <div class="ui-zone-new__count">${t('zones.card.tiles', { n, s: plural(n, 's') })}</div>
          <div class="ui-zone-new__actions">
            <button class="ui-zone-card__btn ui-zone-card__btn--primary" data-act="adopt" ${n === 0 ? 'disabled' : ''}>${t('zones.btn.adopt')}</button>
            <button class="ui-zone-card__btn" data-act="cancel-new">${t('zones.btn.cancel')}</button>
          </div>
        </div>
      `;
    } else {
      newZone = `<button class="ui-zone-new-btn" data-act="start-new">${t('zones.btn.new')}</button>`;
    }

    const emptyHint = plots.length === 0 && !selecting
      ? `<div class="ui-zones__empty">${t('zones.empty')}</div>` : '';

    this.root.innerHTML = `
      <div class="ui-zones__head">
        <div class="ui-zones__title">${t('zones.title')}</div>
        <button class="ui-zones__close" data-act="close" title="${t('zones.btn.close')}">×</button>
      </div>
      <div class="ui-zones__tip">${t('zones.tip')}</div>
      ${newZone}
      ${emptyHint}
      <div class="ui-zones__list">${cards}</div>
    `;
    // Re-focus the name input if it was the active element pre-render so
    // typing isn't interrupted by the dirty-rebuild.
    this.newPlotNameInput = this.root.querySelector('[data-id="new-name"]') as HTMLInputElement | null;
  }

  private onClick = (e: Event) => {
    const t = e.target as HTMLElement;
    // Renaming + new-name typing should NOT trigger card-click dive.
    if (t.tagName === 'INPUT') return;
    const btn = t.closest('[data-act]') as HTMLElement | null;
    if (btn) {
      e.stopPropagation();
      this.handleAction(btn.dataset.act!, btn.dataset.idx);
      return;
    }
    // Bare card click (not on a button) — toggle SELECTION of that plot.
    // Selecting a plot turns on the edit-by-hover UX in the world: hover
    // over a member tile = remove (×), hover over an adjacent free tile
    // = add (+). To dive in, the player clicks the Enter button.
    const card = t.closest('[data-plot-idx]') as HTMLElement | null;
    if (card) {
      const idx = Number(card.dataset.plotIdx);
      const cur = this.sim.state.zonesEditPlotIdx;
      this.sim.selectPlotForEdit(cur === idx ? null : idx);
    }
  };

  private onInput = (e: Event) => {
    const inp = e.target as HTMLInputElement;
    if (inp.dataset.renameIdx != null && e.type === 'change') {
      this.sim.renamePlot(Number(inp.dataset.renameIdx), inp.value);
    }
  };

  private handleAction(act: string, idxStr?: string) {
    const idx = idxStr != null ? Number(idxStr) : -1;
    switch (act) {
      case 'close':
        this.sim.exitZonesMode();
        break;
      case 'start-new':
        this.sim.startPlotSelect();
        break;
      case 'cancel-new':
        this.sim.cancelPlotSelect();
        break;
      case 'adopt': {
        const name = this.newPlotNameInput?.value;
        this.sim.confirmPlot(name);
        break;
      }
      case 'delete': {
        const p = this.sim.state.plots[idx];
        const name = (p as any)?.name ?? `Plot ${idx + 1}`;
        if (confirm(t('zones.confirmDelete', { name }))) {
          this.sim.deletePlot(idx);
        }
        break;
      }
      case 'enter':
        this.enterPlot(idx);
        break;
    }
  }

  private enterPlot(idx: number) {
    // Close the manager first so the camera dive isn't fighting the overlay.
    this.sim.exitZonesMode();
    this.sim.enterPlotView(idx);
  }
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
